import { useAppDispatch, useAppSelector } from 'store/hooks';
import React from 'react';
import { Box, Step, StepButton, StepLabel, Stepper } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { AwardTemplate } from 'components/customers/models/Award';
import { Perk } from 'components/loyalty/models/PerkModel';
import { perkApi } from 'components/loyalty/PerkApi';
import {
    MESSAGE_MARKETING_PERK_CREATE_ERROR,
    MESSAGE_MARKETING_PERK_CREATE_SUCCESS,
    MESSAGE_MARKETING_PERK_TEMPLATE_CREATE_ERROR,
    MESSAGE_MARKETING_PERK_TEMPLATE_CREATE_SUCCESS,
    MESSAGE_MARKETING_PERK_TEMPLATE_UPDATE_ERROR,
    MESSAGE_MARKETING_PERK_TEMPLATE_UPDATE_SUCCESS,
    MESSAGE_MARKETING_PERK_TRIGGER_ADD_ERROR,
    MESSAGE_MARKETING_PERK_TRIGGER_ADD_SUCCESS
} from 'config/messages';
import { useSnackbar } from 'notistack';
import { IconButton } from 'ui/buttons/IconButton';
import { Column, Row } from 'ui/Flex';
import { perkGeneralDefaultFormValues, PerkGeneralForm, PerkGeneralFormData } from './forms/PerkGeneralForm';
import { PerkReviewForm, TemplateFormData } from './forms/PerkReviewForm';
import { PerkSettingsForm, PerkSettingsFormData } from './forms/PerkSettingsForm';
import { PerkAddEffectFormData, PerkClubForm } from './forms/PerkClubForm';
import { generateCreatePerkBody, generatePerkGrantImpactBody, generatePerkRevokeImpactBody } from './helpers';
import { rulesApi } from 'components/rules/rulesApi';
import { toggleCreateModal } from 'store/perks/perksSlice';
import {
    generateAwardRedemptionFormData,
    generateSettingsFormData,
    generateVisualisationFormData
} from './award-enrichments/utils';
import { AwardRedemptionFormData, PerkRedemptionForm } from './forms/RedemptionForm/PerkRedemptionForm';
import {
    PerkVisualisationForm,
    PerkVisualisationFormData
} from 'components/perks/forms/PerkVisualisationForm';
import { perkTemplateApi } from './templates/PerkTemplateApi';
import { PerkTemplateMode } from './templates/models/PerkTemplate';
import {
    createSuccess as createTemplateSuccess,
    updateSuccess as updateTemplateSuccess
} from 'store/perk-templates/perkTemplatesSlice';
import logger from 'lib/logger';

export interface PerksCreationStepperProps {
    onClose: () => void;
    onSubmit: ({ perk, stepFour }?: { perk: Perk; stepFour?: boolean }) => void;
    currencySymbol: string;
    primaryActionBackgroundColour: string;
    initialGeneralData?: Partial<PerkGeneralFormData>;
    duplicatePerk?: Perk | null;
    templateMode?: PerkTemplateMode;
}

const createAdHoc = async (body: Partial<Perk>) => {
    const perkResult = await perkApi.create({ body });
    if (!perkResult.ok) {
        throw new Error(perkResult.body.message);
    }
    return perkResult;
};

const createPseudoCurrency = async (body: Partial<Perk>) => {
    const perkResult = await perkApi.create({ body });
    if (!perkResult.ok) {
        throw new Error(perkResult.body.message);
    }
    return perkResult;
};

const createStampCard = async (body: Partial<Perk>) => {
    const perkResult = await perkApi.create({ body });

    return perkResult;
};

const createPerk = (perk: Partial<Perk>, settingsData: PerkSettingsFormData) => {
    switch (settingsData.perkType) {
        case AwardTemplate.PSEUDO_CURRENCY:
            return createPseudoCurrency(perk);
        case AwardTemplate.STAMP_CARD:
            return createStampCard(perk);
        default:
            return createAdHoc(perk);
    }
};

export const PerksCreationStepper: React.FC<PerksCreationStepperProps> = ({
    onClose,
    onSubmit,
    initialGeneralData = {},
    currencySymbol,
    duplicatePerk,
    primaryActionBackgroundColour,
    templateMode = { type: 'create-perk' }
}) => {
    const { enqueueSnackbar } = useSnackbar();
    const { settings } = useAppSelector(state => state.settings);
    const [steps, setSteps] = React.useState([
        'Define',
        'Configure',
        'Visualisation',
        'Redemption',
        'Review'
    ]);
    const [perkIsCreated, setPerkIsCreated] = React.useState(false);
    const [isLoadingAddTrigger, setIsLoadingAddTrigger] = React.useState(false);
    const [activeStep, setActiveStep] = React.useState(0);
    const isTemplateMode = templateMode.type !== 'create-perk';
    const isViewMode = templateMode.type === 'view-template';
    const editTemplate =
        templateMode.type === 'edit-template' || templateMode.type === 'view-template'
            ? templateMode.template
            : null;
    const sourcePerk = duplicatePerk || (editTemplate ? (editTemplate.perk as Perk) : null);
    const [generalData, setGeneralData] = React.useState<PerkGeneralFormData>({
        ...perkGeneralDefaultFormValues,
        ...initialGeneralData,
        ...(sourcePerk && {
            title: sourcePerk.title,
            order: sourcePerk.priority,
            internalTitle: sourcePerk.internalTitle,
            description: sourcePerk.visualisation.description
        })
    });
    const [perkId, setPerkId] = React.useState();
    const [settingsData, setSettingsData] = React.useState<PerkSettingsFormData>(
        sourcePerk && generateSettingsFormData(sourcePerk)
    );
    const [visualisationData, setVisualisationData] = React.useState<PerkVisualisationFormData>(
        sourcePerk && generateVisualisationFormData(sourcePerk)
    );
    const [redemptionData, setRedemptionData] = React.useState<AwardRedemptionFormData>(
        sourcePerk && generateAwardRedemptionFormData(sourcePerk)
    );
    const [isLoading, setIsLoading] = React.useState(false);
    const dispatch = useAppDispatch();

    const handleStepClick = React.useCallback(
        (index: number) => () => {
            if (!perkIsCreated) {
                setActiveStep(index);
            }
        },
        [perkIsCreated]
    );
    const handleGeneralSubmit = React.useCallback((data: PerkGeneralFormData) => {
        setGeneralData(data);
        setActiveStep(1);
    }, []);
    const handleSettingsSubmit = React.useCallback(
        (data: PerkSettingsFormData) => {
            setSettingsData(data);
            setVisualisationData({ ...visualisationData, perkType: data.perkType });
            setActiveStep(2);
        },
        [visualisationData]
    );
    const handleVisualisationSubmit = React.useCallback((data: PerkVisualisationFormData) => {
        setVisualisationData(data);
        setActiveStep(3);
    }, []);
    const handleRedemptionSubmit = React.useCallback((values: AwardRedemptionFormData) => {
        setRedemptionData(values);
        setActiveStep(4);
    }, []);
    const handlePrevious = React.useCallback(() => {
        setActiveStep(activeStep - 1);
    }, [activeStep]);

    const handleCreatePerk = React.useCallback(async () => {
        setIsLoading(true);
        const body = generateCreatePerkBody(
            generalData,
            settingsData,
            redemptionData,
            visualisationData,
            settings?.region?.timezone,
            !!settings?.app?.awardsScreenBarcodePosition,
            !!settings?.physicalStampsEnabled
        );
        try {
            const result = await createPerk(body, settingsData);
            enqueueSnackbar(MESSAGE_MARKETING_PERK_CREATE_SUCCESS, { variant: 'success' });
            return onSubmit({ perk: result.body });
        } catch (_e) {
            enqueueSnackbar(MESSAGE_MARKETING_PERK_CREATE_ERROR, { variant: 'error' });
        } finally {
            setIsLoading(false);
        }
    }, [
        generalData,
        settingsData,
        redemptionData,
        visualisationData,
        settings?.region?.timezone,
        settings?.app?.awardsScreenBarcodePosition,
        settings?.physicalStampsEnabled,
        onSubmit,
        enqueueSnackbar
    ]);

    const handleCreatePerkContinueAddTrigger = React.useCallback(async () => {
        setIsLoadingAddTrigger(true);
        if (perkIsCreated) {
            setActiveStep(5);
        } else {
            const body = generateCreatePerkBody(
                generalData,
                settingsData,
                redemptionData,
                visualisationData,
                settings?.region?.timezone,
                !!settings?.app?.awardsScreenBarcodePosition
            );
            try {
                const result = await createPerk(body, settingsData);
                setPerkId(result.body._id);
                enqueueSnackbar(MESSAGE_MARKETING_PERK_CREATE_SUCCESS, { variant: 'success' });
                onSubmit({ perk: result.body, stepFour: true });
                setPerkIsCreated(true);
                setActiveStep(5);
            } catch (_e) {
                enqueueSnackbar(MESSAGE_MARKETING_PERK_CREATE_ERROR, { variant: 'error' });
            } finally {
                setIsLoadingAddTrigger(false);
            }
        }
    }, [
        generalData,
        onSubmit,
        perkIsCreated,
        redemptionData,
        settings?.region?.timezone,
        settings?.app?.awardsScreenBarcodePosition,
        settingsData,
        visualisationData,
        enqueueSnackbar
    ]);

    const handleSaveTemplate = React.useCallback(
        async (values: TemplateFormData) => {
            setIsLoading(true);
            const perkBody = generateCreatePerkBody(
                generalData,
                settingsData,
                redemptionData,
                visualisationData,
                settings?.region?.timezone,
                !!settings?.app?.awardsScreenBarcodePosition,
                !!settings?.physicalStampsEnabled
            );
            const templateBody = {
                name: values.templateName,
                description: values.templateDescription,
                perk: perkBody
            };
            try {
                if (editTemplate) {
                    const result = await perkTemplateApi.update(editTemplate._id, { body: templateBody });
                    if (result.ok) {
                        dispatch(updateTemplateSuccess(result.body));
                        enqueueSnackbar(MESSAGE_MARKETING_PERK_TEMPLATE_UPDATE_SUCCESS, {
                            variant: 'success'
                        });
                    } else {
                        throw new Error(result.body.message);
                    }
                } else {
                    const result = await perkTemplateApi.create({ body: templateBody });
                    if (result.ok) {
                        dispatch(createTemplateSuccess(result.body));
                        enqueueSnackbar(MESSAGE_MARKETING_PERK_TEMPLATE_CREATE_SUCCESS, {
                            variant: 'success'
                        });
                    } else {
                        throw new Error(result.body.message);
                    }
                }
                onSubmit();
            } catch (e) {
                logger.error(e.message, e);
                enqueueSnackbar(
                    editTemplate
                        ? MESSAGE_MARKETING_PERK_TEMPLATE_UPDATE_ERROR
                        : MESSAGE_MARKETING_PERK_TEMPLATE_CREATE_ERROR,
                    { variant: 'error' }
                );
            } finally {
                setIsLoading(false);
            }
        },
        [
            generalData,
            settingsData,
            redemptionData,
            visualisationData,
            settings?.region?.timezone,
            settings?.app?.awardsScreenBarcodePosition,
            settings?.physicalStampsEnabled,
            editTemplate,
            dispatch,
            onSubmit,
            enqueueSnackbar
        ]
    );

    const handleAddTriggerToPerk = React.useCallback(
        async (values: PerkAddEffectFormData) => {
            const {
                grantPerkFromImpact,
                revokePerkFromImpact,
                audienceIds,
                revokePerkFromImpactExclude,
                revokePerkFromImpactInclude,
                grantPerkFromImpactExclude,
                grantPerkFromImpactInclude
            } = values;
            setIsLoadingAddTrigger(true);
            try {
                const bodyImpact = generatePerkGrantImpactBody(
                    audienceIds,
                    perkId,
                    grantPerkFromImpactInclude,
                    grantPerkFromImpactExclude
                );
                const bodyRevoke = generatePerkRevokeImpactBody(
                    audienceIds,
                    perkId,
                    revokePerkFromImpactInclude,
                    revokePerkFromImpactExclude
                );

                if (grantPerkFromImpact && revokePerkFromImpact) {
                    await rulesApi.create({ body: bodyImpact });
                    await rulesApi.create({ body: bodyRevoke });
                }
                if (grantPerkFromImpact && !revokePerkFromImpact) {
                    await rulesApi.create({ body: bodyImpact });
                }
                if (revokePerkFromImpact && !grantPerkFromImpact) {
                    await rulesApi.create({ body: bodyRevoke });
                }

                dispatch(toggleCreateModal());
                enqueueSnackbar(MESSAGE_MARKETING_PERK_TRIGGER_ADD_SUCCESS, { variant: 'success' });
            } catch (_e) {
                enqueueSnackbar(MESSAGE_MARKETING_PERK_TRIGGER_ADD_ERROR, { variant: 'error' });
            } finally {
                setIsLoadingAddTrigger(false);
            }
        },
        [dispatch, perkId, enqueueSnackbar]
    );

    const onPerkTypeChange = React.useCallback(
        (perkType: AwardTemplate) => {
            if (perkType === AwardTemplate.CLUB) {
                if (steps.length < 6) {
                    setSteps(previousSteps => [...previousSteps, 'Audience']);
                }
            } else if (steps.length === 6) {
                setSteps(['Define', 'Configure', 'Visualisation', 'Redemption', 'Review']);
            }
        },
        [steps]
    );
    const hasFiveSteps = React.useMemo(() => steps.length === 6, [steps]);

    const templateInitialValues = React.useMemo(
        () => ({
            templateName: editTemplate?.name || generalData.internalTitle || generalData.title || '',
            templateDescription: editTemplate?.description || ''
        }),
        [editTemplate, generalData.internalTitle, generalData.title]
    );

    const reviewLabel = React.useMemo(() => {
        if (isViewMode) {
            return undefined;
        }
        if (isTemplateMode) {
            return editTemplate ? 'Update Template' : 'Save Template';
        }
        return hasFiveSteps ? undefined : 'Save & Close';
    }, [isViewMode, isTemplateMode, editTemplate, hasFiveSteps]);

    return (
        <Box width="100%" maxWidth={1600}>
            <Column>
                <Row valign="flex-start" align="center">
                    <Box flex={1} maxWidth={500}>
                        <Stepper nonLinear alternativeLabel activeStep={activeStep}>
                            {steps.map((item, index) => (
                                <Step key={`step-button-${item}`} completed={activeStep > index}>
                                    <StepButton
                                        disableRipple
                                        disabled={activeStep < index}
                                        onClick={handleStepClick(index)}
                                        color="inherit"
                                    >
                                        <StepLabel>{item}</StepLabel>
                                    </StepButton>
                                </Step>
                            ))}
                        </Stepper>
                    </Box>
                    <Box position="absolute" top={0} right={0}>
                        <IconButton size="large" onClick={onClose}>
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </Row>
                <PerkGeneralForm
                    open={activeStep === 0}
                    onSubmit={handleGeneralSubmit}
                    initialValues={generalData}
                    isTemplateMode={isTemplateMode}
                    readOnly={isViewMode}
                />
                <PerkSettingsForm
                    open={activeStep === 1}
                    clickPrevious={handlePrevious}
                    onSubmit={handleSettingsSubmit}
                    onPerkTypeChange={onPerkTypeChange}
                    initialValues={settingsData}
                    currencySymbol={currencySymbol}
                    primaryActionBackgroundColour={primaryActionBackgroundColour}
                    title={generalData && generalData.title}
                    isTemplateMode={isTemplateMode}
                    isEditTemplate={!!editTemplate}
                    readOnly={isViewMode}
                />
                <PerkVisualisationForm
                    open={activeStep === 2}
                    onSubmit={handleVisualisationSubmit}
                    isLoading={isLoading}
                    primaryActionBackgroundColour={primaryActionBackgroundColour}
                    initialValues={visualisationData}
                    onPrevious={handlePrevious}
                    isTemplateMode={isTemplateMode}
                    readOnly={isViewMode}
                />
                {activeStep === 3 && (
                    <PerkRedemptionForm
                        initialData={redemptionData}
                        onSubmit={handleRedemptionSubmit}
                        clickPrevious={handlePrevious}
                        currencySymbol={currencySymbol}
                        perkType={settingsData?.perkType}
                        auto={settingsData?.auto}
                        isTemplateMode={isTemplateMode}
                        readOnly={isViewMode}
                    />
                )}
                <PerkReviewForm
                    open={activeStep === 4}
                    showButtonStepFive={isTemplateMode ? false : hasFiveSteps}
                    submit={handleCreatePerk}
                    clickPrevious={handlePrevious}
                    submitContinueAddTrigger={handleCreatePerkContinueAddTrigger}
                    isLoading={isLoading}
                    isLoadingContinueAddTrigger={isLoadingAddTrigger}
                    label={reviewLabel}
                    data={{ ...generalData, ...settingsData, ...visualisationData }}
                    primaryActionBackgroundColour={primaryActionBackgroundColour}
                    isTemplateMode={isTemplateMode}
                    templateInitialValues={templateInitialValues}
                    onTemplateSubmit={handleSaveTemplate}
                    readOnly={isViewMode}
                />
                <PerkClubForm
                    open={activeStep === 5}
                    onSubmit={handleAddTriggerToPerk}
                    onClose={onClose}
                    isLoading={isLoadingAddTrigger}
                />
            </Column>
        </Box>
    );
};
